import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Volume2, VolumeX, X } from "lucide-react";

/**
 * Full-screen intro splash. Plays once per browser session (sessionStorage),
 * autoplays muted (Chrome/Safari block audio-autoplay), user can unmute or
 * skip. Fades out and reveals whatever's underneath when the video ends or
 * the user skips.
 *
 * Usage: place at the very top of Home.jsx render — the Home content sits
 * behind it and appears when the splash unmounts.
 */
const STORAGE_KEY = "dt_intro_shown_v3";

export default function IntroSplash() {
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    // Only run on client after mount — avoids SSR/hydration surprises.
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
      // Respect users who prefer reduced motion.
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        return;
      }
      setVisible(true);
    } catch {
      // sessionStorage unavailable — still show once.
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setFading(true);
    setTimeout(() => setVisible(false), 420);
  };

  const handleEnded = () => dismiss();

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  if (!visible) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] bg-black grid place-items-center transition-opacity duration-500 ${fading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      data-testid="intro-splash"
    >
      <video
        ref={videoRef}
        src="/intro.mp4"
        autoPlay
        muted={muted}
        playsInline
        onEnded={handleEnded}
        onError={() => setVideoBroken(true)}
        className={`max-h-screen max-w-screen w-auto h-auto object-contain ${videoBroken ? "hidden" : ""}`}
        data-testid="intro-splash-video"
      />

      {/* Graceful fallback if the browser can't decode the video (rare). */}
      {videoBroken && (
        <div className="text-center text-white/90 px-6" data-testid="intro-splash-fallback">
          <div className="font-serif text-5xl md:text-7xl mb-3">DyNamic Tax</div>
          <div className="text-xs uppercase tracking-[0.35em] text-white/60">Welcome</div>
        </div>
      )}

      {/* Skip button */}
      <button
        onClick={dismiss}
        className="absolute top-6 right-6 flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md text-white text-xs uppercase tracking-widest hover:bg-white/20 transition-colors"
        data-testid="intro-splash-skip"
        aria-label="Skip intro"
      >
        Skip <X size={14} />
      </button>

      {/* Unmute / mute toggle */}
      <button
        onClick={toggleMute}
        className="absolute bottom-6 right-6 h-11 w-11 rounded-full bg-white/10 backdrop-blur-md text-white grid place-items-center hover:bg-white/20 transition-colors"
        data-testid="intro-splash-mute"
        aria-label={muted ? "Unmute intro" : "Mute intro"}
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>
    </div>,
    document.body,
  );
}
